import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Container,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  IconButton,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Link, useNavigate } from "react-router-dom";
import ApiServices from "../../Utils/ApiServices/ApiServices";
import ClinicLogo from "../../Assests/cosmos  Logo.png";
import CryptoJS from "crypto-js";

const OtpInput = ({ otp, setOtp }) => {
  const boxesRef = useRef([]);

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== "" && index < 5) {
      boxesRef.current[index + 1].focus();
    }
  };

  const handleBackspace = (e, index) => {
    if (e.keyCode === 8 && index > 0 && otp[index] === "") {
      boxesRef.current[index - 1]?.focus();
    }
  };
  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = pasteData.split("").map((char, index) => char);
    setOtp(newOtp);
    
    newOtp.forEach((value, index) => {
        boxesRef.current[index].value = value;
    });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}  onPaste={handlePaste}>
      {[...Array(6)].map((_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (boxesRef.current[index] = el)}
          value={otp[index] || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (!isNaN(value) && value >= 0 && value <= 9) {
              handleOtpChange(index, value);
            }
          }}
          onKeyDown={(e) => handleBackspace(e, index)}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center" },
            type: "number",
            pattern: "[0-9]*",
          }}
          sx={{
            width: 50,
            height: 50,
            "& .MuiInputBase-input": { textAlign: "center" },
            "& input[type=number]": {
              "-moz-appearance": "textfield",
            },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
              {
                "-webkit-appearance": "none",
                margin: 0,
              },
          }}
        />
      ))}
    </Box>
  );
};

const PatientLogin = () => {
  const navigate = useNavigate();
  const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState(null);
  const [encryptedUserTypeSave, setencryptedUserTypeSave] = useState("");
  const [encryptedAccessTokenSave, setencryptedAccessTokenSave] = useState("");
  const [encryptedRefreshTokenSave, setencryptedRefreshTokenSave] =
    useState("");
  const [patientTokentoFetchData, setpatientTokentoFetchData] = useState("");
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOtp("");

    try {
      const payload = {
        user_role: "patient",
        username: username,
        email: username,
        password: password,
        client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
        scope: process.env.REACT_APP_PATIENT_SCOPES,
        grant_type: "password",
      };

      const { data, error } = await ApiServices.post(
        "/oauth2/default/token",
        payload,
        {
          "Content-Type": "application/x-www-form-urlencoded",
        }
      );

      if (error) {
        setError(
          error.response?.data?.error_description || "Something went wrong"
        );
      } else {
        const response = await ApiServices.post(
          "/apis/default/api/otpsend",
          { username: username },
          { "Content-Type": "application/json" }
        );

        setOtpMessage("OTP sent successfully");
        setOtpDialogOpen(true);

        const encryptedUserType = CryptoJS.AES.encrypt(
          "Patient",
          secretKey
        ).toString();
        const encryptedAccessToken = CryptoJS.AES.encrypt(
          data.access_token,
          secretKey
        ).toString();
        const encryptedRefreshToken = CryptoJS.AES.encrypt(
          data.refresh_token,
          secretKey
        ).toString();
        setpatientTokentoFetchData(data.access_token);
        setencryptedUserTypeSave(encryptedUserType);
        setencryptedAccessTokenSave(encryptedAccessToken);
        setencryptedRefreshTokenSave(encryptedRefreshToken);
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Failed to login, please check your login credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp&&otp?.length===6) {
        handleOtpSubmit();
    }
  }, [otp]);

  const handleOtpSubmit = async () => {
    try {
      const enteredOtp = otp.join("");
      const response = await ApiServices.post(
        "/apis/default/api/verifyotpsent",
        { username: username, otp: enteredOtp },
        { "Content-Type": "application/json" }
      );

      const { status, msg } = response.data.data;

      if (status) {
        setOtpMessage("OTP verified successfully");
        setOtp("");
        setOtpDialogOpen(false);
        sessionStorage.setItem("LoginUserType", encryptedUserTypeSave);
        sessionStorage.setItem(
          "LoginUserAccessToken",
          encryptedAccessTokenSave
        );
        sessionStorage.setItem(
          "LoginUserRefreshToken",
          encryptedRefreshTokenSave
        );
        const patientResponse = await ApiServices.get(
          "/apis/default/portal/patient",
          {
            Authorization: `Bearer ${patientTokentoFetchData}`,
          }
        );
        const userData = patientResponse.data.data;
        const encryptedUserData = CryptoJS.AES.encrypt(
          JSON.stringify(userData),
          secretKey
        ).toString();
        sessionStorage.setItem("LoginUserDetails", encryptedUserData);

        setTimeout(() => {
          navigate("/patient/dashboard");
        }, 3000);
      } else {
        setError("OTP verification failed");
        setOtp("");
      }
    } catch (error) {
      setError("OTP verification failed");
      setOtp("");
    }
  };

  const handleSnackbarClose = () => {
    setError(null);
    setOtpMessage(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #012A4A 0%, #025E73 35%, #028391 65%, #03A9A3 100%)",
      }}
    >
      <Container
        component="main"
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Grid
          container
          spacing={5}
          sx={{ justifyContent: "center", alignItems: "center" }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <img
                src={ClinicLogo}
                alt="Doctor"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Box>
            <Typography
              variant="body1"
              align="center"
              sx={{ mb: 4, color: "#fff" }}
            >
              Login to manage patient records efficiently and securely.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                bgcolor: "background.default",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                p: 4,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Avatar
                  sx={{ bgcolor: "#05B6B3", mb: 2, width: 80, height: 80 }}
                >
                  <AccountCircleIcon sx={{ width: 60, height: 60 }} />
                </Avatar>
              </Box>
              <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                Patient Login
              </Typography>
              <form onSubmit={handleLogin} style={{ width: "100%" }}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In"
                  )}
                </Button>
                <Grid container justifyContent="space-between" sx={{ mt: 1 }}>
                  <Grid item>
                    <Link
                      to="/register"
                      variant="body2"
                      style={{ color: "#05B6B3" }}
                    >
                     Click here to Create New Account
                    </Link>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" sx={{ mt: 1 }}>
                  <Grid item>
                    <Link to="/forgot-password" variant="body2" style={{ color: "#05B6B3" }}>
                      Forgot password?
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link
                      to="/provider-emr-login"
                      variant="body2"
                      style={{ color: "#05B6B3" }}
                    >
                      Doctor Login
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
        <Snackbar
          open={error !== null || otpMessage !== null}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleSnackbarClose}
            severity={error ? "error" : "success"}
          >
            {error || otpMessage}
          </MuiAlert>
        </Snackbar>
        <Dialog
          open={otpDialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setOtpDialogOpen(false);
            }
          }}
          disableEscapeKeyDown
        >
          <DialogTitle>Enter OTP</DialogTitle>
          <DialogContent>
            <OtpInput otp={otp} setOtp={setOtp} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOtpDialogOpen(false)} color="primary">
              Cancel
            </Button>
            {/* <Button onClick={handleOtpSubmit} color="primary">
              Submit
            </Button> */}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default PatientLogin;
